import React from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { BiBell, BiMessageDetail, BiMinus, BiPencil, BiPlus, BiTransfer, BiUndo } from 'react-icons/bi';

// ── Types ------------------------------------------------------------------ //

export interface RemovedStopSummary {
  id: number;
  label: string;
  address: string | null;
}

export interface EditedStopSummary {
  id: number;
  label: string;
  changes: { field: string; from: string; to: string }[];
}

interface RunEditReviewDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  applying: boolean;
  applyError: string | null;
  /** Whether the run has been dispatched (drives the notify copy). */
  isDispatched: boolean;
  driverName: string | null;
  orderChanged: boolean;
  /** Draft order as display labels, e.g. "1. Daniel Artus". */
  newOrderLabels: string[];
  removedStops: RemovedStopSummary[];
  editedStops: EditedStopSummary[];
  /** Appointments staged as new stops (same display shape as removals). */
  addedStops: RemovedStopSummary[];
}

// ── Component --------------------------------------------------------------- //

const RunEditReviewDialog: React.FC<RunEditReviewDialogProps> = ({
  open,
  onClose,
  onApply,
  applying,
  applyError,
  isDispatched,
  driverName,
  orderChanged,
  newOrderLabels,
  removedStops,
  editedStops,
  addedStops,
}) => {
  const theme = useTheme();

  const changeCount = removedStops.length + editedStops.length + addedStops.length + (orderChanged ? 1 : 0);

  const impacts: { icon: React.ReactNode; text: React.ReactNode }[] = [];
  if (removedStops.length > 0) {
    impacts.push({
      icon: <BiUndo size={15} color={theme.palette.info.main} />,
      text: (
        <>
          <strong>{removedStops.map(s => s.label).join(', ')}</strong>
          {removedStops.length > 1 ? ' return ' : ' returns '}
          to the pending appointments queue.
        </>
      ),
    });
  }
  if (isDispatched && addedStops.length > 0) {
    impacts.push({
      icon: <BiMessageDetail size={15} color={theme.palette.info.main} />,
      text: (
        <>
          <strong>{addedStops.map(s => s.label).join(', ')}</strong>
          {addedStops.length > 1 ? ' receive ' : ' receives '}
          a confirmation request by SMS.
        </>
      ),
    });
  }
  if (isDispatched && driverName && changeCount > 0) {
    impacts.push({
      icon: <BiBell size={15} color={theme.palette.info.main} />,
      text: (
        <>
          <strong>{driverName}</strong> sees the updated stop list in the driver app.
        </>
      ),
    });
  }

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth='sm' fullWidth>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
          Review changes
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {isDispatched
            ? 'This run is dispatched. Nothing changes until you apply.'
            : 'Nothing changes until you apply.'}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 1 }}>
        {changeCount === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 3 }}>
            No changes to apply.
          </Typography>
        ) : (
          <>
            {removedStops.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 ? <Divider /> : null}
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.error.light + '30',
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <BiMinus size={14} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      Stop removed — {s.label}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {s.address || 'No address set'} · returns to the pending queue
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))}

            {addedStops.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 || removedStops.length > 0 ? <Divider /> : null}
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.success.light + '30',
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <BiPlus size={14} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      Stop added — {s.label}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {s.address || 'No address set'}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))}

            {editedStops.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 || removedStops.length > 0 || addedStops.length > 0 ? <Divider /> : null}
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.warning.light + '30',
                    color: 'warning.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <BiPencil size={14} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      Stop edited — {s.label}
                    </Typography>
                    {s.changes.map((c, ci) => (
                      <Typography key={ci} variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                        {c.field}:{' '}
                        <Box component='span' sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                          {c.from || '—'}
                        </Box>
                        {' → '}
                        <Box component='span' sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {c.to || '—'}
                        </Box>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </React.Fragment>
            ))}

            {orderChanged ? (
              <>
                {removedStops.length > 0 || editedStops.length > 0 || addedStops.length > 0 ? <Divider /> : null}
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.info.light + '30',
                    color: 'info.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <BiTransfer size={14} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      Stop order changed
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {newOrderLabels.join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : null}

            {impacts.length > 0 ? (
              <Box sx={{ mt: 1.5, mb: 1, p: 1.75, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant='overline' sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: 0.6,
                  display: 'block',
                  mb: 0.75,
                  lineHeight: 1.5,
                }}>
                  What happens
                </Typography>
                {impacts.map((im, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
                    <Box sx={{ mt: '2px', flexShrink: 0 }}>{im.icon}</Box>
                    <Typography variant='body2' color='text.secondary' sx={{ '& strong': { color: 'text.primary' } }}>
                      {im.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </>
        )}

        {applyError ? (
          <Alert severity='error' sx={{ mt: 1, mb: 1 }}>
            {applyError}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button size='small' variant='outlined' onClick={onClose} disabled={applying}>
          Keep editing
        </Button>
        <Button
          size='small'
          variant='contained'
          color='primary'
          onClick={onApply}
          disabled={applying || changeCount === 0}
        >
          {applying ? 'Applying…' : 'Apply changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RunEditReviewDialog;
